import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { jwtDecode } from "jwt-decode";
import useStore from "../../store/useStore";
import { getMobileNumber } from "../../utils/localStorage";
import { getPediaDoctorDetails } from "../account/service";
import { TatvaPediaToken } from "./service";
import { trackApiAuthRejected, trackPediaAuthRetry, trackPediaTokenMint } from "../../utils/zymedSessionDiagnostics";

/*
 * ZymedPedia token lifecycle.
 *
 * The Pedia access token is a short-lived JWT from the Diginext auth server, and the
 * user-management backend rejects it with a 403 once it expires. The token used to be
 * minted only when it was missing and never refreshed, so a user who stayed logged in
 * past its lifetime got "Let's get you back in! Re-login" on the next article, even
 * though the native app session was still valid.
 *
 * This module keeps the token fresh without bothering the user:
 *   - mintPediaToken() shares one in-flight request, so screens that mount together
 *     (Pedia home + trending) no longer mint two tokens. Every new token registers its
 *     own device session on the backend, so duplicate mints used to fill the device limit.
 *   - Requests to user-management that carry a Pedia token get it swapped for a fresh
 *     one shortly before it expires.
 *   - A 403 on such a request mints a new token and retries once. Only a 403 that
 *     survives the retry reaches the screen's own handler.
 */

const PEDIA_TOKEN_KEY = "tatvaPediaToken";
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;

let inFlightMint: Promise<string | null> | null = null;

const decodeExpiry = (token: string): number | null => {
    try {
        const { exp } = jwtDecode<{ exp?: number }>(token);
        return exp ? exp * 1000 : null;
    } catch {
        return null;
    }
};

const isPediaToken = (token: string): boolean => {
    if (token === localStorage.getItem(PEDIA_TOKEN_KEY)) return true;
    try {
        const { iss } = jwtDecode<{ iss?: string }>(token);
        const issuer = process.env.REACT_APP_TATVA_PEDIA;
        return !!iss && !!issuer && iss.replace(/\/$/, "") === issuer.replace(/\/$/, "");
    } catch {
        return false;
    }
};

const isExpiringSoon = (token: string): boolean => {
    const expiresAt = decodeExpiry(token);
    return expiresAt !== null && expiresAt - Date.now() < REFRESH_BEFORE_EXPIRY_MS;
};

const storeToken = (token: string) => {
    localStorage.setItem(PEDIA_TOKEN_KEY, token);
    useStore.getState().setTatvaPediaToken(token);
};

type MintReason = "missing" | "expiring" | "retry_after_403";

/** Mints a new Pedia token. Concurrent callers share the same request. */
export const mintPediaToken = (reason: MintReason = "missing"): Promise<string | null> => {
    if (inFlightMint) return inFlightMint;
    const startedAt = Date.now();
    const oldToken = localStorage.getItem(PEDIA_TOKEN_KEY);
    const report = (
        outcome: "success" | "failed",
        extra: { failStage?: any; httpStatus?: number; newToken?: string | null } = {}
    ) => trackPediaTokenMint({ reason, outcome, durationMs: Date.now() - startedAt, oldToken, ...extra });

    inFlightMint = (async () => {
        let stage: "doctor_details" | "token_api" = "doctor_details";
        try {
            const res: any = await getPediaDoctorDetails(getMobileNumber());
            if (res?.status !== 200) {
                report("failed", { failStage: "doctor_details", httpStatus: res?.status });
                return null;
            }
            const b2cId = res?.data?.body?.b2c_id;
            const mobileNumber = res?.data?.body?.mobile_number || getMobileNumber();
            if (!b2cId || !mobileNumber) {
                report("failed", { failStage: "missing_b2c_or_mobile" });
                return null;
            }
            localStorage.setItem("b2c_id", b2cId);
            stage = "token_api";
            const response = await TatvaPediaToken({
                username: `+${String(mobileNumber).replace(/^\+/, "")}`,
                password: `uuid:${b2cId}`,
            });
            if (!response?.access_token) {
                report("failed", { failStage: "no_access_token" });
                return null;
            }
            storeToken(response.access_token);
            report("success", { newToken: response.access_token });
            return response.access_token;
        } catch (error: any) {
            console.error("Failed to mint TatvaPedia token:", error);
            report("failed", { failStage: stage, httpStatus: error?.response?.status });
            return null;
        } finally {
            inFlightMint = null;
        }
    })();
    return inFlightMint;
};

/** Returns a Pedia token that is valid for at least another minute, minting one if needed. */
export const getFreshPediaToken = async (): Promise<string | null> => {
    const current = localStorage.getItem(PEDIA_TOKEN_KEY);
    if (current && !isExpiringSoon(current)) return current;
    return (await mintPediaToken(current ? "expiring" : "missing")) || current;
};

type PediaRequestConfig = AxiosRequestConfig & { _pediaRetried?: boolean };

const bearerOf = (config: AxiosRequestConfig): string | null => {
    const header = (config.headers as any)?.Authorization ?? (config.headers as any)?.authorization;
    return typeof header === "string" && header.startsWith("Bearer ") ? header.slice(7) : null;
};

const isPediaRequest = (config?: AxiosRequestConfig): boolean => {
    const userManagement = process.env.REACT_APP_USER_MANAGEMENT;
    if (!config?.url || !userManagement || !config.url.startsWith(userManagement)) return false;
    const token = bearerOf(config);
    return !!token && token !== "null" && token !== "undefined" ? isPediaToken(token) : false;
};

const withToken = (config: PediaRequestConfig, token: string): PediaRequestConfig => ({
    ...config,
    headers: { ...(config.headers as any), Authorization: `Bearer ${token}` },
});

const isRejected = (response?: AxiosResponse) => response?.status === 403 || response?.data?.status === 403;

/** Re-mints after a 403 and retries once, reporting what the server said each time. */
const retryWithNewToken = async (
    config: PediaRequestConfig,
    rejection: { httpStatus?: number; body?: any }
): Promise<AxiosResponse | null> => {
    trackApiAuthRejected({ tokenKind: "pedia", url: config.url, attempt: "first", token: bearerOf(config), ...rejection });
    const token = await mintPediaToken("retry_after_403");
    if (!token) {
        trackPediaAuthRetry({ url: config.url, outcome: "mint_failed" });
        return null;
    }
    const retryConfig = withToken({ ...config, _pediaRetried: true }, token);
    try {
        const retried = await axios.request(retryConfig);
        if (isRejected(retried)) {
            trackApiAuthRejected({ tokenKind: "pedia", url: config.url, attempt: "retry", token, httpStatus: retried.status, body: retried.data });
            trackPediaAuthRetry({ url: config.url, outcome: "still_rejected", httpStatus: retried.status, newToken: token });
        } else {
            trackPediaAuthRetry({ url: config.url, outcome: "recovered", httpStatus: retried.status, newToken: token });
        }
        return retried;
    } catch (error: any) {
        if (error?.response?.status === 403) {
            trackApiAuthRejected({ tokenKind: "pedia", url: config.url, attempt: "retry", token, httpStatus: 403, body: error.response.data });
            trackPediaAuthRetry({ url: config.url, outcome: "still_rejected", httpStatus: 403, newToken: token });
        }
        throw error;
    }
};

let installed = false;

/** Installs the refresh/retry interceptors on the default axios instance used by Pedia services. */
export const installPediaTokenInterceptors = () => {
    if (installed) return;
    installed = true;

    axios.interceptors.request.use(async (config: any) => {
        if (!isPediaRequest(config)) return config;
        const token = bearerOf(config)!;
        if (!isExpiringSoon(token)) return config;
        const fresh = await getFreshPediaToken();
        return fresh && fresh !== token ? withToken(config, fresh) : config;
    });

    axios.interceptors.response.use(
        async (response) => {
            const config = response.config as PediaRequestConfig;
            if (response?.data?.status === 403 && !config._pediaRetried && isPediaRequest(config)) {
                return (await retryWithNewToken(config, { httpStatus: response.status, body: response.data })) || response;
            }
            return response;
        },
        async (error) => {
            const config = error?.config as PediaRequestConfig | undefined;
            if (error?.response?.status === 403 && config && !config._pediaRetried && isPediaRequest(config)) {
                const retried = await retryWithNewToken(config, { httpStatus: 403, body: error.response.data });
                if (retried) return retried;
            }
            return Promise.reject(error);
        }
    );
};
